import * as React from 'react';
import { Button } from '@/components/ui/button';
import { FileBrowser } from '@/components/files';
import type { UseWs } from '@/hooks/use-ws';
import { useSessionStore } from '@/stores/session';
import { emitToast } from '@/components/shell';
import type { ExtrasTabId } from '@/components/extras/extras';
import {
  LazyAdminPane,
  LazyBrowserPane,
  LazyCronApprovalsPane,
  LazyLoopsPane,
  LazyOfficePane,
  LazyExtrasPane,
  LazyGitPane,
  LazyModelsPane,
  LazyObservabilityPane,
  LazyPluginsPane,
  LazyProvidersPane,
  LazySettingsPane,
  LazySetupPane,
  LazyTerminalPane,
  LazyTestingPane,
  LazyTodosPane,
  LazyUsagePane,
  PaneFallback,
} from '@/components/panes/lazy-panes';
import type { InspectorTabId } from './panes';

// InspectorHost: renders one REAL Inspector pane inside a tiling tab.
// Same components and props as the right InspectorPanel, so a tiling tab
// and the sidebar tab are the same live surface. Session-scoped tool panes
// (terminal/git/browser) bind to the workspace session; without one they
// show a real "create session" action instead of a dead pane.
export function InspectorHost({
  tab,
  sessionId,
  ws,
  onOpenSession,
  onOpenInspectorTab,
}: {
  tab: InspectorTabId;
  sessionId: string | null;
  ws?: UseWs;
  onOpenSession?: (id: string) => void;
  onOpenInspectorTab: (id: InspectorTabId) => void;
}) {
  return (
    <React.Suspense fallback={<PaneFallback pane={tab} />}>
      <LazyTab tab={tab} sessionId={sessionId} ws={ws} onOpenSession={onOpenSession} onOpenInspectorTab={onOpenInspectorTab} />
    </React.Suspense>
  );
}

// LazyTab — the tab switch itself stays synchronous (pure conditional), so
// only the chunk download suspends inside the boundary above.
function LazyTab({
  tab,
  sessionId,
  ws,
  onOpenSession,
  onOpenInspectorTab,
}: {
  tab: InspectorTabId;
  sessionId: string | null;
  ws?: UseWs;
  onOpenSession?: (id: string) => void;
  onOpenInspectorTab: (id: InspectorTabId) => void;
}) {
  if (tab === 'providers') return <LazyProvidersPane />;
  // REQ-043 — Files is a first-class pane tab (same FileBrowser as the
  // sidebar Files page, session-scoped with a remount key).
  if (tab === 'files') {
    if (!sessionId) return <NeedsSessionPane pane={tab} onOpenSession={onOpenSession} />;
    return <FileBrowser key={sessionId} sessionId={sessionId} />;
  }
  if (tab === 'models') return <LazyModelsPane />;
  if (tab === 'usage') return <LazyUsagePane onOpenSession={onOpenSession} />;
  if (tab === 'settings') return <LazySettingsPane />;
  // REQ-163/164/165/166 — Agent Hub, Orchestration, Vault, Memory and
  // Skills are not panes anymore (Settings modal sections); REQ-167 — the
  // Archify pane path is gone too (its rail icon opens a standalone modal);
  // REQ-168 — Design left the pane system entirely (its own page).
  if (tab === 'testing') return <LazyTestingPane />;
  if (tab === 'auth') return <LazyAdminPane />;
  if (tab === 'setup') return <LazySetupPane />;
  if (tab === 'plugins') return <LazyPluginsPane />;
  if (tab === 'observability') return <LazyObservabilityPane />;
  if (tab === 'cron') return <LazyCronApprovalsPane />;
  // REQ-202 — the Loops console (same component as Settings → Loops).
  if (tab === 'loops') return <LazyLoopsPane />;
  // REQ-204 — the Office pane (slice 2): the live row view.
  if (tab === 'office') return <LazyOfficePane />;
  if (tab === 'todos') return <LazyTodosPane sessionId={sessionId} onOpenSession={onOpenSession} />;
  if (tab === 'extras') {
    return <LazyExtrasPane onOpenTab={(t: ExtrasTabId) => onOpenInspectorTab(t)} />;
  }
  if (tab === 'terminal' || tab === 'git' || tab === 'browser') {
    if (!sessionId || (tab === 'terminal' && !ws)) return <NeedsSessionPane pane={tab} onOpenSession={onOpenSession} />;
    if (tab === 'terminal') {
      // REQ-162 — the shared tab wrapper is a scrolling block (`h-full
      // overflow-auto`), so the emulator's viewport collapses there: give it
      // the tab body's full height (same shell the browser uses, REQ-037).
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <LazyTerminalPane key={sessionId} sessionId={sessionId} ws={ws as UseWs} />
        </div>
      );
    }
    if (tab === 'git') return <LazyGitPane key={sessionId} sessionId={sessionId} />;
    // REQ-037 — the shared tab wrapper above is a scrolling block (`h-full
    // overflow-auto`), so BrowserPane's flex-1 is inert there and the page
    // area collapses. This flex shell hands the pane the tab body's full
    // height; other inspector tabs keep the scrolling wrapper untouched.
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <LazyBrowserPane key={sessionId} sessionId={sessionId} />
      </div>
    );
  }
  return null;
}

function NeedsSessionPane({ pane, onOpenSession }: { pane: string; onOpenSession?: (id: string) => void }) {
  const createSession = useSessionStore((s) => s.createSession);
  const [busy, setBusy] = React.useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const id = await createSession();
      if (id) {
        emitToast(`Session ${id.slice(0, 12)} created`);
        onOpenSession?.(id);
      } else {
        emitToast('Could not create a session');
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <p className="text-xs text-muted-foreground">
        {pane === 'terminal' ? 'Terminal' : pane === 'git' ? 'Git' : pane === 'files' ? 'Files' : 'Browser'} needs a session for its working directory.
      </p>
      <Button variant="default" size="sm" className="h-7 text-xs" disabled={busy} onClick={create}>
        {busy ? 'Creating…' : 'New session'}
      </Button>
    </div>
  );
}
